import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { smmSubscriptionsApi } from '../lib/api';
import { boostState, actionToast, routeMissing, serverError, legacyUrl } from '../lib/v3Backlog';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { TableScroll, StatTile, StatGrid } from '../components/ResponsiveTable';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarClock, RefreshCw, Pause, Play, AlertCircle, Info, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * SMM > Subscriptions - the replacement for the legacy Thymeleaf /smmSubscriptionOrders/ (r16o3 v3-backlog, future todo 4).
 *
 * What V1 shows, kept: every subscription order of the account (the SMM Orders page leaves them out), its panel status,
 * quantity and the slots it went to, and a Pause / Resume per order. V1's Delete column links to a route that does not
 * exist, so there is no delete here either.
 *
 * What V1 does not show, added from the server's read of the same data: whether the order is boosting now. V1 keeps no
 * "paused" flag - pausing takes the order's username out of the boost list (setting 1305) of each slot the order was
 * assigned to, resuming puts it back - so the server counts the slots whose list holds it, and the page says "boosting on
 * 2 of 3 slots" instead of leaving the operator to guess what the last click did. A button that would change nothing (Pause
 * on an order already paused everywhere) is disabled and says why.
 *
 * Server: r16o3 GET /uiapi/smm-subscription-orders, POST /uiapi/smm-subscription-orders/{id}/pause | resume. A server
 * without them (404 / 405) gets a banner pointing to the legacy page. Switch: lib/r14Switch r16o3UiEnabled (App.js route).
 */

const STATE_TONE = {
  boosting: 'border-success/40 text-success',
  partly: 'border-warning/40 text-warning',
  paused: 'border-border text-muted-foreground',
  none: 'border-border text-muted-foreground',
  unknown: 'border-border text-muted-foreground',
};

const STATE_WORD = { boosting: 'Boosting', partly: 'Partly paused', paused: 'Paused', none: 'No slots', unknown: 'Unknown' };

// The bar's colour is the badge's state (lib/v3Backlog boostState), not k / n on its own: an order boosting on every slot that
// has an Instagram account reads "Boosting" and its bar is green, even when one assigned slot has no account (k < n).
const BAR_TONE = { boosting: 'bg-success', partly: 'bg-warning', paused: 'bg-muted-foreground/40', none: 'bg-muted-foreground/40', unknown: 'bg-muted-foreground/40' };

function StateBadge({ state }) {
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap', STATE_TONE[state.kind])} data-testid="sub-state">
      {STATE_WORD[state.kind]}
    </Badge>
  );
}

function BoostBar({ row, state }) {
  const n = row.assigned_slots_known;
  const k = row.boosting_slots;
  if (n === null || n === undefined || k === null || k === undefined || n === 0) {
    return <span className="text-xs text-muted-foreground">{state.label}</span>;
  }
  const pct = Math.round((k / n) * 100);
  return (
    <div className="min-w-[120px]" title={state.label}>
      <span className="tnum whitespace-nowrap text-sm text-foreground">{k}<span className="text-muted-foreground"> / {n} slots</span></span>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={cn('h-full rounded-full', BAR_TONE[state.kind])} style={{ width: `${pct}%` }} data-testid="sub-bar" data-kind={state.kind} />
      </div>
      {row.unknown_slots > 0 && (
        <p className="mt-0.5 text-[11px] text-muted-foreground">{row.unknown_slots} without an Instagram account</p>
      )}
    </div>
  );
}

function shortTarget(row) {
  const t = row.target_url || '';
  return t.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') || '—';
}

function when(v) {
  return v ? String(v).replace('T', ' ').slice(0, 16) : '—';
}

// V1's four slot columns (Assigned To Slots, Processed Success / Failed / Invalid) are csv lists; here each count carries its
// list as a tooltip (review should-fix, 2026-10-24: only the assigned list was shown).
function csvTip(what, csv) {
  return csv ? `${what}: ${csv}` : `${what}: none recorded`;
}

function Actions({ row, state, supported, pendingKey, onAct }) {
  const busy = (a) => pendingKey === `${a}:${row.order_id}`;
  const why = (a) => {
    if (!supported) return 'Pause / resume make no change on an account with the SMM scraping service on';
    if (a === 'pause' && !state.canPause) return state.kind === 'none' ? 'The order has no assigned slot' : 'Already paused on every slot';
    if (a === 'resume' && !state.canResume) {
      if (state.kind === 'none') return 'The order has no assigned slot';
      return row.unknown_slots > 0 ? 'Already boosting on every slot that has an Instagram account' : 'Already boosting on every slot';
    }
    return a === 'pause' ? `Take ${row.username || 'this account'} out of the boost list of its slots` : `Put ${row.username || 'this account'} back in the boost list of its slots`;
  };
  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button size="sm" variant="outline" disabled={!supported || !state.canPause || !!pendingKey} title={why('pause')}
        onClick={() => onAct('pause', row.order_id)} data-testid="sub-pause" aria-label={`Pause order #${row.order_id}`}>
        {busy('pause') ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />}
        <span className="ml-1">Pause</span>
      </Button>
      <Button size="sm" variant="outline" disabled={!supported || !state.canResume || !!pendingKey} title={why('resume')}
        onClick={() => onAct('resume', row.order_id)} data-testid="sub-resume" aria-label={`Resume order #${row.order_id}`}>
        {busy('resume') ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        <span className="ml-1">Resume</span>
      </Button>
    </div>
  );
}

export default function SmmSubscriptionsPage() {
  usePageTitle('Subscriptions');
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = useState(null);

  const q = useQuery({
    queryKey: ['smm-subscription-orders'],
    queryFn: () => smmSubscriptionsApi.list().then((r) => r.data),
    retry: (n, err) => !routeMissing(err) && n < 1,
  });

  const mut = useMutation({
    mutationFn: ({ action, orderId }) => (action === 'resume' ? smmSubscriptionsApi.resume(orderId) : smmSubscriptionsApi.pause(orderId)),
    onMutate: ({ action, orderId }) => setPendingKey(`${action}:${orderId}`),
    onSuccess: (res, { action, orderId }) => {
      // eslint-disable-next-line no-console
      console.info(`[R16O3-SUBSCRIPTIONS] ${action} #${orderId}: boosting ${res.data?.boosting_slots} of ${res.data?.assigned_slots_known}`);
      toast.success(actionToast(action, orderId, res.data));
      queryClient.invalidateQueries({ queryKey: ['smm-subscription-orders'] });
    },
    onError: (err, { action, orderId }) => {
      // eslint-disable-next-line no-console
      console.warn(`[R16O3-SUBSCRIPTIONS] ${action} #${orderId} failed: HTTP ${err?.response?.status || 'none'}`);
      toast.error(`Could not ${action} order #${orderId}: ${serverError(err)}`);
    },
    onSettled: () => setPendingKey(null),
  });

  const act = (action, orderId) => mut.mutate({ action, orderId });
  const data = q.data;
  const rows = Array.isArray(data?.orders) ? data.orders : [];
  const supported = data ? data.pause_supported !== false : true;
  const states = rows.map((r) => boostState(r));
  const count = (kind) => states.filter((s) => s.kind === kind).length;
  const legacy = legacyUrl('/smmSubscriptionOrders/');

  const header = (
    <PageHeader
      icon={CalendarClock}
      title="Subscriptions"
      description={data
        ? `${data.total} subscription order${data.total === 1 ? '' : 's'} from your SMM panel. Pause takes the account out of its slots' boosting list; Resume puts it back.`
        : 'Monthly boosting orders from your SMM panel, with pause and resume.'}
      actions={(
        <Button variant="outline" size="icon" onClick={() => q.refetch()} disabled={q.isFetching} title="Refresh" aria-label="Refresh">
          <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} />
        </Button>
      )}
    />
  );

  if (q.isError && routeMissing(q.error)) {
    return (
      <div data-testid="smm-subscriptions-page">
        {header}
        <div className="surface flex items-start gap-3 p-4" data-testid="sub-route-missing">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <p className="text-sm text-muted-foreground">
            This server does not have the subscriptions API yet (it comes with server r16o3). The legacy page still works:{' '}
            <a className="text-foreground underline break-all" href={legacy} target="_blank" rel="noreferrer">{legacy}</a>
          </p>
        </div>
      </div>
    );
  }

  const mobile = rows.map((r, i) => (
    <div key={r.id ?? r.order_id} className="surface p-3" data-testid="sub-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium tnum text-foreground">#{r.order_id}{r.username && <span className="ml-2 font-normal text-muted-foreground">@{r.username}</span>}</p>
          <p className="truncate text-xs text-muted-foreground">{shortTarget(r)}</p>
        </div>
        <StateBadge state={states[i]} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <BoostBar row={r} state={states[i]} />
        <span className="text-xs text-muted-foreground">{r.status || '—'}</span>
      </div>
      <div className="mt-3">
        <Actions row={r} state={states[i]} supported={supported} pendingKey={pendingKey} onAct={act} />
      </div>
    </div>
  ));

  return (
    <div data-testid="smm-subscriptions-page">
      {header}

      <StatGrid cols={4} className="mb-5">
        <StatTile label="Subscriptions" value={data ? data.total : '—'} icon={CalendarClock} testId="sub-stat-total" />
        <StatTile label="Boosting" value={data ? count('boosting') : '—'} tone="success" sub="on every assigned slot" testId="sub-stat-boosting" />
        <StatTile label="Partly paused" value={data ? count('partly') : '—'} tone="warning" sub="on some slots only" testId="sub-stat-partly" />
        <StatTile label="Paused" value={data ? count('paused') : '—'} tone="muted" sub="on every assigned slot" testId="sub-stat-paused" />
      </StatGrid>

      {data && !supported && (
        <div className="surface mb-4 flex items-start gap-2 p-3 text-sm text-muted-foreground" data-testid="sub-unsupported">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          The SMM scraping service is on for this account, and pause / resume make no change for such an account (the same rule
          as the legacy page), so the buttons are off.
        </div>
      )}
      {data && data.boost_state === false && (
        <p className="mb-3 text-xs text-muted-foreground" data-testid="sub-no-state">The server is not reading the boosting state right now, so it shows as unknown.</p>
      )}

      {q.isError && (
        <div className="surface flex items-center gap-2 p-4 text-danger" data-testid="sub-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-sm">Could not load subscription orders. {serverError(q.error)}</span>
        </div>
      )}

      {q.isLoading && (
        <div className="space-y-2" data-testid="sub-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary/60" />)}
        </div>
      )}

      {data && rows.length === 0 && (
        <div className="surface p-8 text-center" data-testid="sub-empty">
          <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No subscription orders. They appear here when your panel sends an order for a subscription service.</p>
        </div>
      )}

      {rows.length > 0 && (
        // Seven columns, every cell one or two short lines that never wrap (the panel status shares a cell with the time the
        // order was placed, the slot counts read "3 assigned / 2 ok · 1 failed"), so the table fits a 1440 px screen with the
        // sidebar and both buttons stay whole; below that width the table scrolls sideways inside its own box.
        <TableScroll mobile={mobile} minWidth="1000px">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Order</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Panel status</TableHead>
                <TableHead>Boosting</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Slots</TableHead>
                <TableHead className="text-right"><span className="sr-only">Pause or resume</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id ?? r.order_id} className="border-border" data-testid="sub-row">
                  <TableCell className="whitespace-nowrap">
                    <p className="tnum font-medium">#{r.order_id}</p>
                    <p className="text-[11px] text-muted-foreground" title={r.panel_user_id ? `panel user ${r.panel_user_id}` : undefined}>
                      {r.service_id !== null && r.service_id !== undefined ? `service ${r.service_id}` : 'service —'}
                      {r.panel_user_id ? ` · user ${r.panel_user_id}` : ''}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <p className="truncate text-foreground">{r.username ? `@${r.username}` : '—'}</p>
                    {/^https?:\/\//i.test(r.target_url || '') ? (
                      <a href={r.target_url} target="_blank" rel="noreferrer noopener" className="inline-flex max-w-full items-center gap-1 text-xs text-primary hover:underline" title={r.target_url}>
                        <span className="truncate">{shortTarget(r)}</span><ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : (
                      <p className="truncate text-xs text-muted-foreground" title={r.target_url || ''}>{shortTarget(r)}</p>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <p className="text-sm text-foreground">{r.status || '—'}</p>
                    <p className="tnum text-[11px] text-muted-foreground" title="placed">{when(r.ordered_at)}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <StateBadge state={states[i]} />
                      <BoostBar row={r} state={states[i]} />
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tnum" data-testid="sub-quantity">
                    {r.quantity ?? '—'}
                    <p className="text-[11px] text-muted-foreground" title="Remains (success), as the legacy page">{r.remaining ?? '—'} left</p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm tnum" data-testid="sub-slots">
                    <span className="cursor-help border-b border-dotted border-current" title={csvTip('assigned', r.assigned_slots_csv)} data-testid="sub-slots-assigned">{r.assigned_slots_count ?? '—'}</span>
                    <span className="text-muted-foreground"> assigned</span>
                    {/* V1's "Processed (Success + Failed)": orderDeliveryProcessedSlotsCount, the slots done so far (ok, failed or invalid).
                        Its own short line, so the cell stays as narrow as its widest line and the table still fits 1440 px. */}
                    {r.delivery_processed_count !== null && r.delivery_processed_count !== undefined && (
                      <p className="text-[11px] text-muted-foreground" title="Processed (success + failed), as the legacy page" data-testid="sub-slots-processed">{r.delivery_processed_count} processed</p>
                    )}
                    <p className="text-[11px]">
                      <span className="cursor-help text-success" title={csvTip('processed success', r.success_slots_csv)} data-testid="sub-slots-ok">{r.success_slots} ok</span>
                      <span className="text-muted-foreground"> · </span>
                      <span className={cn('cursor-help', r.failed_slots > 0 ? 'text-danger' : 'text-muted-foreground')} title={csvTip('processed failed', r.failed_slots_csv)} data-testid="sub-slots-failed">{r.failed_slots} failed</span>
                      {r.invalid_slots > 0 && <><span className="text-muted-foreground"> · </span><span className="cursor-help text-warning" title={csvTip('processed invalid', r.invalid_slots_csv)} data-testid="sub-slots-invalid">{r.invalid_slots} invalid</span></>}
                    </p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Actions row={r} state={states[i]} supported={supported} pendingKey={pendingKey} onAct={act} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      )}

      {data && data.truncated && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="sub-truncated">Showing the newest {rows.length} of {data.total}.</p>
      )}
      {rows.length > 0 && (
        // A V1 limitation, said rather than hidden (review should-fix, 2026-10-24): the server wrote each order's slots as
        // "slotN" from the slot's POSITION in the account's slot list, while pause / resume and the count match slots by NAME.
        <p className="mt-3 max-w-3xl text-xs text-muted-foreground" data-testid="sub-slot-names">
          Slots are matched by name, as on the legacy page. The server recorded each order's slots as slot1, slot2, ... by their
          position in this account's slot list, so if a slot's name differs from its position, the count and Pause / Resume
          concern the slot with that name.
        </p>
      )}
    </div>
  );
}
